import React from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { usePinnedItems } from '../../services/pinned-items-store';
import type { PinnedItem } from '../../types';
import { PinnedItemRow } from '../pinned-item-row/PinnedItemRow';

import messages from './messages';
import { PinnedWorkSkeleton } from './PinnedWorkSkeleton';

const styles = cssMap({
	emptyState: {
		width: '100%',
		borderStyle: 'dashed',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.xxlarge'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	message: {
		flex: '1',
	},
});

interface PinnedWorkSectionProps {
	cloudId: string;
	product?: string;
	/** Called when the "+" button or, in the empty state, the "Pin work" button is clicked. */
	onPinWorkClick: () => void;
	/** Called when a pinned item's "Start chat" button is clicked. */
	onStartChat: (item: PinnedItem) => void;
}

/**
 * Pinned work section of the "For You" tab. When no items are pinned, renders only the
 * empty-state prompt. Once at least one item is pinned, renders a "Pinned work" heading
 * with a "+" button (navigates to search) followed by the list of pinned items instead.
 */
export const PinnedWorkSection = ({
	cloudId,
	product,
	onPinWorkClick,
	onStartChat,
}: PinnedWorkSectionProps): React.JSX.Element => {
	const intl = useIntl();
	const { items, isLoading, unpin } = usePinnedItems({ cloudId, product });

	return (
		<Stack testId="rovo-for-you-pinned-work-section" space="space.150">
			{isLoading ? (
				<PinnedWorkSkeleton />
			) : items.length === 0 ? (
				<Box xcss={styles.emptyState} testId="rovo-for-you-pinned-work-empty-state">
					<Flex alignItems="center" justifyContent="space-between" gap="space.150">
						<Box xcss={styles.message}>
							<Text color="color.text.subtle">
								{intl.formatMessage(messages.pinnedWorkEmptyStateMessage)}
							</Text>
						</Box>
						<Button
							appearance="default"
							onClick={onPinWorkClick}
							testId="rovo-for-you-pinned-work-pin-button"
						>
							{intl.formatMessage(messages.pinnedWorkPinButtonLabel)}
						</Button>
					</Flex>
				</Box>
			) : (
				<>
					<Flex alignItems="center" justifyContent="space-between">
						<Heading size="xxsmall" as="h4">
							{intl.formatMessage(messages.pinnedWorkHeading)}
						</Heading>
						<IconButton
							icon={AddIcon}
							label={intl.formatMessage(messages.pinnedWorkAddButtonLabel)}
							appearance="subtle"
							onClick={onPinWorkClick}
							testId="rovo-for-you-pinned-work-add-button"
						/>
					</Flex>
					<Stack testId="rovo-for-you-pinned-work-list">
						{items.map((item) => (
							<PinnedItemRow
								key={item.id}
								type={item.pinType}
								title={item.name}
								onOpen={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
								onStartChat={() => onStartChat(item)}
								onUnpin={() => unpin(item.ari)}
							/>
						))}
					</Stack>
				</>
			)}
		</Stack>
	);
};
